import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase } from 'lucide-react';

const CTABanner = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es';

  const services = lang === 'en'
    ? ['Digital Marketing', 'Branding & Identity', 'Social Media', 'Event Production', 'Content Creation', 'Project Management']
    : ['Marketing Digital', 'Identidad de Marca', 'Redes Sociales', 'Producción de Eventos', 'Creación de Contenido', 'Gestión de Proyectos'];

  return (
    <section
      data-testid="cta-banner"
      className="relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0F1C2E 0%, #1a2a4a 50%, #0d1825 100%)' }}
    >
      {/* Ambient glow blobs */}
      <div style={{
        position: 'absolute', top: '-4rem', left: '-4rem',
        width: '30rem', height: '30rem', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(125,70,140,0.2) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-4rem', right: '-4rem',
        width: '26rem', height: '26rem', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(78,191,191,0.15) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      {/* Geometric diamond accents */}
      <div className="absolute top-10 right-20 w-4 h-4 bg-[#F2785C]/35" style={{ transform: 'rotate(45deg)' }} />
      <div className="absolute bottom-10 left-16 w-3 h-3 bg-[#4EBFBF]/35" style={{ transform: 'rotate(45deg)' }} />
      <div className="absolute top-1/2 right-[12%] w-2 h-2 bg-[#7D468C]/50" style={{ transform: 'rotate(45deg)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[560px]">

          {/* ── LEFT: Image panel ── */}
          <div className="relative overflow-hidden lg:rounded-none" style={{ minHeight: '400px' }}>
            {/* Replace this image src with your own when ready */}
            <img
              id="cta-banner-image"
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=900&q=80&auto=format&fit=crop"
              alt="Mikel Media team at work"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.75) saturate(1.1)' }}
            />
            {/* Gradient overlay blending into content side */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to right, rgba(13,24,37,0.2) 0%, rgba(13,24,37,0.85) 100%)',
            }} />
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to top, rgba(13,24,37,0.6) 0%, transparent 50%)',
            }} />

            {/* Floating service pill badges on the image */}
            <div className="absolute bottom-8 left-6 right-6 flex flex-wrap gap-2">
              {services.slice(0, 4).map((s, i) => (
                <span key={i} style={{
                  fontSize: '0.7rem',
                  padding: '0.3rem 0.75rem',
                  borderRadius: '2rem',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                  background: i % 2 === 0
                    ? 'rgba(125,70,140,0.45)'
                    : 'rgba(78,191,191,0.35)',
                  color: i % 2 === 0 ? '#d4a8e0' : '#9fe8e8',
                  border: `1px solid ${i % 2 === 0 ? 'rgba(125,70,140,0.5)' : 'rgba(78,191,191,0.4)'}`,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  animation: `mmFadeInUp 0.6s ${0.1 * i}s ease-out both`,
                }}>
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Content panel ── */}
          <div className="flex flex-col justify-center py-16 px-8 lg:px-12">

            {/* Label */}
            <div className="flex items-center gap-3 mb-5" style={{ animation: 'mmFadeInUp 0.7s ease-out both' }}>
              <div className="w-2.5 h-2.5 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
              <span className="text-xs font-semibold text-[#F2785C] tracking-widest uppercase font-poppins">
                {lang === 'en' ? 'Work with us' : 'Trabaja con nosotros'}
              </span>
            </div>

            {/* Headline */}
            <h2
              className="font-poppins font-black text-4xl sm:text-5xl leading-tight mb-5"
              style={{
                animation: 'mmFadeInUp 0.7s 0.1s ease-out both',
                opacity: 0,
              }}
            >
              <span style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #d4a8e0 50%, #4EBFBF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {lang === 'en' ? 'Ready to Elevate Your Brand?' : '¿Listo para Elevar tu Marca?'}
              </span>
            </h2>

            {/* Body text */}
            <p
              className="text-white/65 text-base sm:text-lg leading-relaxed mb-6 font-source-sans max-w-md"
              style={{ animation: 'mmFadeInUp 0.7s 0.2s ease-out both', opacity: 0 }}
            >
              {lang === 'en'
                ? 'From brand strategy and digital marketing to event production — we craft experiences that move audiences and grow businesses in Puerto Rico & USA.'
                : 'Desde estrategia de marca y marketing digital hasta producción de eventos — creamos experiencias que mueven audiencias y hacen crecer negocios en Puerto Rico y EE.UU.'}
            </p>

            {/* Service list mini */}
            <ul
              className="mb-10 space-y-2"
              style={{ animation: 'mmFadeInUp 0.7s 0.3s ease-out both', opacity: 0 }}
            >
              {services.slice(0, 3).map((s, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-white/60 font-source-sans">
                  <div className="w-1.5 h-1.5 bg-[#4EBFBF] flex-shrink-0" style={{ transform: 'rotate(45deg)' }} />
                  {s}
                </li>
              ))}
            </ul>

            {/* Buttons */}
            <div
              className="flex flex-wrap gap-4"
              style={{ animation: 'mmFadeInUp 0.7s 0.4s ease-out both', opacity: 0 }}
            >
              {/* Liquid glass portfolio button */}
              <button
                onClick={() => navigate('/portafolio')}
                data-testid="cta-portfolio-btn"
                className="group relative overflow-hidden flex items-center gap-2 px-8 py-4 rounded-full font-poppins font-semibold text-sm tracking-wide text-white transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, rgba(125,70,140,0.35) 0%, rgba(78,191,191,0.25) 100%)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  border: '1px solid rgba(78,191,191,0.35)',
                  boxShadow: [
                    '0 8px 32px rgba(78,191,191,0.15)',
                    '0 2px 8px rgba(125,70,140,0.2)',
                    'inset 0 1px 0 rgba(255,255,255,0.15)',
                  ].join(', '),
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(125,70,140,0.5) 0%, rgba(78,191,191,0.4) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(78,191,191,0.6)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(78,191,191,0.3), inset 0 1px 0 rgba(255,255,255,0.18)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(125,70,140,0.35) 0%, rgba(78,191,191,0.25) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(78,191,191,0.35)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(78,191,191,0.15), 0 2px 8px rgba(125,70,140,0.2), inset 0 1px 0 rgba(255,255,255,0.15)';
                }}
              >
                {/* Gloss */}
                <span className="absolute inset-x-0 top-0 h-1/2 rounded-t-full pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 100%)' }} />
                <Briefcase size={15} className="relative" />
                <span className="relative">{lang === 'en' ? 'View Our Work' : 'Ver Portafolio'}</span>
                <ArrowRight size={14} className="relative group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Solid CTA contact */}
              <button
                onClick={() => navigate('/contacto')}
                data-testid="cta-contact-btn"
                className="group flex items-center gap-2 px-8 py-4 rounded-full font-poppins font-semibold text-sm tracking-wide text-white transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#F2785C]/25"
                style={{
                  background: 'linear-gradient(135deg, #F2785C, #7D468C)',
                }}
              >
                {t('cta.button')}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes mmFadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default CTABanner;
